import React from "react";
import { Helmet } from "react-helmet-async";
import { MessageCircle, Phone, Mail, MapPin, Clock } from "lucide-react";
import { COMPANY_EMAIL, COMPANY_CITY } from "../data/mock";
import { Reveal } from "../components/Reveal";
import LeadForm from "../components/LeadForm";
import { useLanguage } from "../i18n/LanguageContext";
import { usePublicContent } from "../i18n/PublicContentContext";

export default function Contact() {
  const { t, lang } = useLanguage();
  const c = t.contact;
  const pub = usePublicContent();
  const ct = pub.contact || {};

  const email = (ct.email && ct.email.trim()) || COMPANY_EMAIL;
  const city = (lang === "hi" ? (ct.city_hi || ct.city_en) : ct.city_en) || COMPANY_CITY;
  const hours = (lang === "hi" ? (ct.hours_hi || ct.hours_en) : ct.hours_en) || c.cards.supportValue;

  const contactCards = [
    {
      icon: Phone,
      label: c.cards.whatsapp,
      value: pub.phones.map((p, i) => (
        <a key={p.tel} href={p.tel} className="block hover:text-[#E84B1C] transition-colors" data-testid={`contact-phone-${i + 1}`}>{p.display}</a>
      )),
    },
    { icon: Mail, label: c.cards.email, value: email },
    { icon: MapPin, label: c.cards.location, value: city },
    { icon: Clock, label: c.cards.support, value: hours },
  ];

  return (
    <div className="bg-white">
      <Helmet>
        <title>Contact Us | Superhost Cloud</title>
        <meta name="description" content="Get in touch with Superhost Cloud for hotel management, OTA and booking solutions." />
      </Helmet>

      {/* Header */}
      <section className="relative pt-36 pb-14">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4F6FA] to-white" />
        <div className="absolute top-10 right-10 w-80 h-80 rounded-full bg-[#E84B1C]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-3xl">
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{c.eyebrow}</span>
            <h1 className="mt-3 font-head text-4xl md:text-6xl font-extrabold leading-tight">
              <span className="text-[#12284B]">{c.title1}</span>{" "}
              <span className="text-[#E84B1C]">{c.title2}</span>
            </h1>
            <p className="mt-5 text-black/70 text-lg max-w-2xl">{c.paragraph}</p>
            <a
              href={pub.waLink()}
              target="_blank"
              rel="noreferrer"
              className="mt-7 inline-flex items-center gap-2 bg-[#E84B1C] hover:bg-[#F0673E] text-white font-head font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:scale-105 glow-orange"
            >
              <MessageCircle size={20} /> {c.whatsappBtn}
            </a>
          </Reveal>
        </div>
      </section>

      {/* Contact cards */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {contactCards.map((card) => {
            const Icon = card.icon;
            return (
              <Reveal key={card.label}>
                <div className="bg-white border border-[#12284B]/10 rounded-2xl p-6 h-full shadow-sm">
                  <div className="h-12 w-12 rounded-xl bg-[#E84B1C]/12 flex items-center justify-center">
                    <Icon size={22} className="text-[#E84B1C]" />
                  </div>
                  <div className="mt-4 text-black/55 text-sm">{card.label}</div>
                  <div className="mt-1 font-head font-semibold text-[#12284B] break-words">{card.value}</div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{c.formEyebrow}</span>
            <h2 className="mt-3 font-head text-3xl md:text-5xl font-bold text-[#12284B] leading-tight">{c.formTitle}</h2>
            <p className="mt-4 text-black/70 text-lg leading-relaxed">{c.formParagraph}</p>
            <div className="mt-8 bg-[#F4F6FA] border border-[#12284B]/10 rounded-2xl p-6">
              <p className="text-black/70 leading-relaxed">
                <span className="font-head font-semibold text-[#E84B1C]">{c.whyTitle}</span>{" "}
                {c.whyText}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <LeadForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
